/**
 * RoomPicker — searchable room link for the task and report forms (D4b).
 *
 * Eighth thin wrapper over the shared ``AsyncCombobox`` (C0), and the
 * only one with dependent parent filters.
 *
 * Rendered shape:
 *   MAD-A · Zimmer 2
 *   4-Zimmer-Wohnung · Wohnhaus Dufourstrasse
 *
 * Server contract:
 *   GET /api/admin/rooms/picker-options
 *       ?q=&unit_id=&property_id=&limit=&offset=   (D4a2)
 *
 * **Hydration comes from the caller, and here there is no alternative.**
 * There is no ``GET /api/admin/rooms/{id}`` in this API, so the
 * fetch-by-id trick the Tenancy and Unit pickers use is unavailable. It
 * is also unnecessary: D4a3 ships ``room_display_name``,
 * ``room_unit_id`` and ``room_property_id`` beside ``room_id`` on every
 * task and report, which is exactly the option shape a picker needs —
 * label to render, parents to check compatibility against.
 *
 * **Why the parents matter.** ``room_unit_id`` / ``room_property_id``
 * describe where the ROOM lives. They are NOT the row's own
 * ``unit_id`` / ``property_id``, which are independent direct links a
 * person chose. The form compares the two to notice that a selected
 * room no longer fits a newly picked parent; if both came from the same
 * place the check would compare a value with itself and never fire.
 *
 * **Nothing is derived.** Choosing a room does not fill in its unit, and
 * choosing a unit does not fill in its property — the same refusal the
 * backend built into the write path. The parents are read to VALIDATE,
 * never to populate.
 *
 * The payload it feeds back is a bare ``room_id`` string.
 */
import React from "react";

import AsyncCombobox from "../../../../components/ui/AsyncCombobox";
import { fetchAdminRoomPickerOptions } from "../../../../api/adminData";

const PAGE_SIZE = 20;

/**
 * "MAD-A · Zimmer 2" — the server already composes this (unit prefix +
 * room name, falling back to the bare name and then to "Zimmer"),
 * because "Zimmer 1" exists in most units and is useless alone. This
 * only unwraps it. Never a UUID.
 * Exported for tests.
 */
export function roomLabel(room) {
  const label = (room?.label || "").trim();
  if (label) return label;
  const name = (room?.name || "").trim();
  if (name) return name;
  return "Zimmer ohne Bezeichnung";
}

/**
 * "4-Zimmer-Wohnung · Wohnhaus Dufourstrasse" — the parents as a
 * secondary line, so two identically named rooms in different buildings
 * are distinguishable. Null when neither parent is known.
 * Exported for tests.
 */
export function roomContext(room) {
  return (
    [
      (room?.unit_label || "").trim() || null,
      (room?.property_label || "").trim() || null,
    ]
      .filter(Boolean)
      .join(" · ") || null
  );
}

/**
 * Does ``room`` still belong under the chosen parents?
 *
 * ``null`` means "cannot tell" — the room carries no parent metadata, so
 * no client-side claim is made and the backend stays the authority.
 * Only a KNOWN mismatch returns false, because clearing a link on a
 * guess is worse than letting the server reject the save.
 *
 * A unit whose own property is unknown (``room_property_id`` null) is
 * NOT judged against a property filter: a room in an unassigned unit is
 * the production shape today, and calling that a conflict would clear
 * links that are perfectly legal.
 * Exported for the form and for tests.
 */
export function roomFitsParents(room, { unitId, propertyId } = {}) {
  if (!room) return null;
  const roomUnit = room.unit_id || room.room_unit_id || null;
  const roomProperty = room.property_id || room.room_property_id || null;

  if (unitId) {
    if (!roomUnit) return null; // unknown parent → no claim
    if (String(roomUnit) !== String(unitId)) return false;
  }
  if (propertyId) {
    if (!roomProperty) return null; // unit has no property, or unknown
    if (String(roomProperty) !== String(propertyId)) return false;
  }
  return true;
}

export default function RoomPicker({
  value,
  onChange,
  /** ``{ id, label, unit_id, property_id }`` for the current link, built
   * by the form from the row's ``room_id`` / ``room_display_name`` /
   * ``room_unit_id`` / ``room_property_id``. */
  selectedOption = null,
  /** Narrow the options. Both optional — with neither, every room in the
   * organisation is searchable, which is a legitimate starting state. */
  unitId = null,
  propertyId = null,
  disabled = false,
}) {
  const [picked, setPicked] = React.useState(null);

  const selected =
    picked && String(picked.id) === String(value || "")
      ? picked
      : selectedOption && String(selectedOption.id) === String(value || "")
        ? selectedOption
        : null;

  // Keyed by the parents as VALUES, not by identity, so the base
  // combobox re-requests when a filter changes but not on every render.
  const fetchPage = React.useCallback(
    ({ query, offset, limit }) =>
      fetchAdminRoomPickerOptions({
        q: query || undefined,
        unitId: unitId || undefined,
        propertyId: propertyId || undefined,
        limit,
        offset,
      }),
    [unitId, propertyId]
  );

  const handleChange = React.useCallback(
    (id, item) => {
      setPicked(item);
      // The full row goes back to the form as well: it carries the
      // room's own parents, which the form needs to re-check the link
      // when a parent changes later.
      onChange(id, item);
    },
    [onChange]
  );

  return (
    <AsyncCombobox
      label="Zimmer (optional)"
      placeholder="— keine —"
      searchPlaceholder="Zimmer nach Name oder Unit suchen …"
      loadingText="Zimmer werden geladen …"
      emptyText="Keine passenden Zimmer gefunden."
      errorText="Zimmer konnten nicht geladen werden."
      value={value}
      selectedItem={selected}
      onChange={handleChange}
      fetchPage={fetchPage}
      getItemId={(r) => r.id}
      getItemLabel={(r) => roomLabel(r)}
      pageSize={PAGE_SIZE}
      disabled={disabled}
      renderItem={(room) => (
        <span className="block min-w-0">
          <span
            title={roomLabel(room)}
            className="block truncate text-[13px] font-medium text-foreground"
          >
            {roomLabel(room)}
          </span>
          {roomContext(room) ? (
            <span
              title={roomContext(room)}
              className="block truncate text-[11px] text-muted-foreground"
            >
              {roomContext(room)}
            </span>
          ) : null}
        </span>
      )}
    />
  );
}
